import { api } from './apiClient';

export interface AiStatus {
  configured: boolean;
  model: string | null;
  capabilities: string[];
}

export interface AiAnswer {
  conversationId: number;
  answer: string;
  /** Which server-side tools produced the answer. Shown to the user. */
  toolsUsed: string[];
  model: string;
  latencyMs: number;
}

export interface Insight {
  id: number;
  category: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO' | 'POSITIVE';
  title: string;
  body?: string | null;
  /** The numbers behind the claim. Rendered so a user can check it. */
  evidence?: Record<string, unknown> | null;
  link?: string | null;
  source: 'RULE' | 'MODEL';
  created_at: string;
}

export const aiService = {
  /**
   * Whether AI is usable on this deployment.
   *
   * The UI must hide the assistant entirely when this reports
   * `configured: false` — offering a button that always errors is worse than
   * offering nothing. Insights are unaffected: they are rule-computed server
   * side and work with no credentials.
   */
  status: (): Promise<AiStatus> => api.get('/ai/status'),

  ask: (prompt: string, conversationId?: number): Promise<AiAnswer> =>
    api.post('/ai/ask', { prompt, conversationId }),

  conversations: () => api.get('/ai/conversations'),

  conversation: (id: number) => api.get(`/ai/conversations/${id}`),

  deleteConversation: (id: number) =>
    api.delete(`/ai/conversations/${id}`).then((r: any) => r?.data ?? r),

  insights: (category?: string): Promise<{ insights: Insight[]; count: number; critical: number }> =>
    api.get(`/ai/insights${category ? `?category=${encodeURIComponent(category)}` : ''}`),

  /** Recompute the deterministic rules. Works with no AI credentials. */
  refreshInsights: () => api.post('/ai/insights/refresh', {}),

  /**
   * Ask the model to analyse the workspace snapshot.
   * Costs a Gemini call, so it is only ever user-initiated.
   */
  generateInsights: (): Promise<{ generated: number; rejected: number; model: string }> =>
    api.post('/ai/insights/generate', {}),

  /** The exact figures the model is given — nothing else reaches it. */
  snapshot: () => api.get('/ai/snapshot'),

  dismissInsight: (id: number) => api.post(`/ai/insights/${id}/dismiss`, {}),
};

export default aiService;
